<template>
  <div>
    <v-btn
      v-can="permissao"
      :disabled="disabled"
      class="
        ma-1
        font-weight-bold
        text-none
        btn-primary
        bg-gradient-primary
        py-3
        px-6
        ms-3
      "
      height="39"
      @click="abrir"
    >
      <v-icon left>{{
        atualizacao ? "mdi-file-edit-outline" : "mdi-file-excel-outline"
      }}</v-icon>
      {{ configuracao.textoBotao || modo("botao") }}
    </v-btn>

    <v-dialog
      v-model="dialog"
      max-width="1080"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      persistent
      scrollable
    >
      <v-card class="excel-import-card">
        <v-card-title class="excel-import-header pa-5">
          <div>
            <div class="text-h6 font-weight-bold">
              {{ configuracao.titulo || modo("titulo") }}
            </div>
            <div class="text-body-2 excel-import-subtitle">
              {{ modo("subtitulo") }}
            </div>
          </div>
          <v-spacer />
          <v-btn
            icon
            :disabled="ocupado"
            :aria-label="t('fechar')"
            @click="fechar"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-0">
          <v-stepper
            v-model="passo"
            flat
            alt-labels
            class="excel-import-stepper"
          >
            <v-stepper-header>
              <v-stepper-step :complete="passo > 1" step="1">{{
                t("passoPreparar")
              }}</v-stepper-step>
              <v-divider />
              <v-stepper-step :complete="passo > 2" step="2">{{
                modo("passoConferir")
              }}</v-stepper-step>
              <v-divider />
              <v-stepper-step step="3">{{
                t("passoConcluido")
              }}</v-stepper-step>
            </v-stepper-header>
            <div
              v-if="erro"
              class="excel-import-aviso excel-import-aviso--error mx-5 mt-4"
              role="alert"
            >
              <v-icon small>mdi-alert-circle-outline</v-icon>
              <span>{{ erro }}</span>
            </div>
            <v-stepper-items>
              <v-stepper-content step="1" class="pa-5">
                <div
                  class="excel-import-aviso excel-import-aviso--primary mb-5"
                >
                  <v-icon small>mdi-file-table-outline</v-icon>
                  <span>{{
                    configuracao.descricao || t("descricaoPadrao")
                  }}</span>
                </div>
                <v-card
                  v-if="atualizacao"
                  outlined
                  class="pa-4 mb-5 excel-import-sheet"
                >
                  <div class="text-subtitle-1 font-weight-bold mb-3">
                    {{ t("selecaoTitulo") }}
                  </div>
                  <v-row dense>
                    <v-col
                      v-for="campo in configuracao.filtros || []"
                      :key="campo.chave"
                      cols="12"
                      md="4"
                    >
                      <v-select
                        v-if="campo.opcoes"
                        v-model="filtro[campo.chave]"
                        :items="campo.opcoes"
                        :label="campo.label"
                        :disabled="ocupado"
                        outlined
                        dense
                        hide-details
                      />
                      <v-text-field
                        v-else
                        v-model.trim="filtro[campo.chave]"
                        :label="campo.label"
                        :disabled="ocupado"
                        clearable
                        outlined
                        dense
                        hide-details
                        @keydown.enter.prevent="verificarSelecao"
                      />
                    </v-col>
                    <v-col cols="12" md="4"
                      ><v-btn
                        block
                        height="39"
                        class="
                          font-weight-bold
                          text-none
                          btn-primary
                          bg-gradient-primary
                          px-6
                        "
                        :loading="consultando"
                        :disabled="ocupado && !consultando"
                        @click="verificarSelecao"
                        >{{ t("verificarSelecao") }}</v-btn
                      ></v-col
                    >
                  </v-row>
                  <div
                    v-if="selecao"
                    class="excel-import-aviso mt-4"
                    :class="
                      selecao.totalElements > limiteRegistros ||
                      !selecao.totalElements
                        ? 'excel-import-aviso--warning'
                        : 'excel-import-aviso--info'
                    "
                    role="status"
                  >
                    <v-icon small>{{
                      selecao.totalElements > limiteRegistros ||
                      !selecao.totalElements
                        ? "mdi-alert-outline"
                        : "mdi-information-outline"
                    }}</v-icon>
                    <span>
                      {{
                        t("selecaoEncontrados", {
                          total: selecao.totalElements,
                        })
                      }}
                      {{
                        selecao.totalElements > limiteRegistros
                          ? t("selecaoLimite", {
                              limite: numero(limiteRegistros),
                            })
                          : !selecao.totalElements
                          ? t("selecaoVazia")
                          : t("selecaoTodos")
                      }}
                    </span>
                  </div>
                  <v-data-table
                    v-if="selecao && selecao.content.length"
                    :headers="
                      configuracao.headersSelecao || headersPreviaPadrao
                    "
                    :items="selecao.content"
                    :items-per-page="-1"
                    hide-default-footer
                    dense
                    class="mt-3"
                  />
                  <div
                    v-if="selecao && selecao.content.length"
                    class="text-caption excel-import-subtitle mt-2"
                  >
                    {{
                      t("selecaoAmostra", {
                        quantidade: selecao.content.length,
                        total: selecao.totalElements,
                      })
                    }}
                  </div>
                </v-card>
                <v-row>
                  <v-col
                    v-for="(aba, index) in configuracao.abas || []"
                    :key="aba.nome"
                    cols="12"
                    md="4"
                  >
                    <v-card
                      outlined
                      class="excel-import-sheet pa-4 fill-height"
                    >
                      <v-avatar
                        size="30"
                        class="excel-import-numero text-body-2 mb-3"
                        >{{ index + 1 }}</v-avatar
                      >
                      <div class="text-subtitle-1 font-weight-bold mb-1">
                        {{ aba.nome }}
                      </div>
                      <div class="text-body-2 excel-import-subtitle">
                        {{ aba.descricao }}
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
                <div class="excel-import-guide mt-4 pa-4">
                  <div class="font-weight-bold mb-2">
                    <v-icon small color="primary" class="mr-1"
                      >mdi-lightbulb-outline</v-icon
                    >
                    {{ t("guiaTitulo") }}
                  </div>
                  <p class="mb-2">
                    {{ t("guiaTexto") }}
                  </p>
                  <p class="mb-0">
                    {{ configuracao.orientacao || t("orientacaoPadrao") }}
                  </p>
                </div>
                <div
                  class="d-flex align-center flex-wrap mt-5"
                  style="gap: 12px"
                >
                  <v-btn
                    class="
                      ma-1
                      font-weight-bold
                      text-none
                      btn-primary
                      bg-gradient-primary
                      px-6
                    "
                    height="39"
                    :loading="gerando"
                    :disabled="
                      (ocupado && !gerando) || (atualizacao && !selecaoValida)
                    "
                    @click="gerarModelo"
                  >
                    <v-icon left>mdi-download</v-icon> {{ t("baixarModelo") }}
                  </v-btn>
                  <v-btn
                    class="
                      ma-1
                      font-weight-bold
                      text-capitalize
                      btn-ls btn-secondary
                      bg-gradient-light
                      px-6
                    "
                    height="39"
                    :disabled="ocupado"
                    @click="passo = 2"
                  >
                    {{ t("jaTenhoArquivo") }}
                    <v-icon right small>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
                <div class="text-caption excel-import-subtitle mt-3">
                  {{
                    t("limites", {
                      registros: numero(limiteRegistros),
                      mb: limiteMB,
                    })
                  }}
                </div>
              </v-stepper-content>

              <v-stepper-content step="2" class="pa-5">
                <div
                  v-if="modeloBaixado"
                  class="text-body-2 mb-4"
                  role="status"
                >
                  <v-icon small color="success" class="mr-1"
                    >mdi-check-circle</v-icon
                  >
                  {{ t("modeloBaixado") }}
                </div>
                <div
                  class="excel-import-drop pa-5"
                  :class="{
                    'excel-import-drop--active': arrastando && !ocupado,
                  }"
                  @dragover.prevent="arrastando = true"
                  @dragleave.prevent="arrastando = false"
                  @drop.prevent="soltarArquivo"
                >
                  <v-file-input
                    :value="arquivo"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    :label="t('arquivoRotulo')"
                    prepend-icon="mdi-file-excel-outline"
                    outlined
                    show-size
                    :disabled="ocupado"
                    :loading="validando"
                    :hint="t('arquivoDica', { mb: limiteMB })"
                    persistent-hint
                    @change="arquivoAlterado"
                  />
                </div>
                <v-progress-linear
                  v-if="validando"
                  indeterminate
                  color="primary"
                  class="mt-4"
                  :aria-label="t('validando')"
                />
                <div v-if="validando" class="text-body-2 mt-2" role="status">
                  {{ t("conferindo") }}
                </div>
                <v-btn
                  v-if="arquivo && !resultadoValidacao && !validando"
                  class="
                    ma-1
                    mt-3
                    font-weight-bold
                    text-none
                    btn-primary
                    bg-gradient-primary
                    px-6
                  "
                  height="39"
                  :disabled="ocupado"
                  @click="validarArquivo"
                  >{{ t("conferirNovamente") }}</v-btn
                >

                <template v-if="resultadoValidacao">
                  <div
                    class="excel-import-aviso mt-5 mb-4"
                    :class="`excel-import-aviso--${situacaoValidacao.tipo}`"
                    role="status"
                  >
                    <v-icon small>{{ situacaoValidacao.icone }}</v-icon>
                    <span>
                      <template v-if="semAlteracoes">{{
                        t("semAlteracoes")
                      }}</template>
                      <template v-else-if="podeAplicar"
                        ><strong>{{
                          modo("prontos", { quantidade: quantidadeAcao })
                        }}</strong>
                        {{ t("confiraPrevia") }}</template
                      >
                      <template v-else
                        ><strong>{{
                          t("reviseInconsistencias", {
                            quantidade: errosValidacao.length,
                          })
                        }}</strong>
                        {{ t("nadaGravado") }}</template
                      >
                    </span>
                  </div>
                  <div
                    v-if="resumoAcoes"
                    class="excel-import-resumo mb-4"
                    role="status"
                  >
                    <span
                      v-for="acao in resumoAcoes"
                      :key="acao.chave"
                      class="excel-import-resumo__item"
                      :class="`excel-import-resumo__item--${acao.chave}`"
                    >
                      <v-icon small>{{ acao.icone }}</v-icon>
                      <strong>{{ numero(acao.quantidade) }}</strong>
                      {{ acao.texto }}
                    </span>
                  </div>
                  <v-card v-if="errosValidacao.length" outlined class="mb-4">
                    <v-card-title class="text-subtitle-2 py-3 d-flex flex-wrap">
                      {{ t("corrigirTitulo") }}
                      <v-spacer />
                      <v-btn
                        class="
                          ma-1
                          font-weight-bold
                          text-capitalize
                          btn-ls btn-secondary
                          bg-gradient-light
                          px-6
                        "
                        height="39"
                        @click="baixarErros"
                        ><v-icon small left>mdi-download</v-icon>
                        {{ t("baixarInconsistencias") }}</v-btn
                      >
                    </v-card-title>
                    <v-data-table
                      :headers="headersErros"
                      :items="errosValidacao"
                      :items-per-page="10"
                      :footer-props="paginacao"
                      dense
                    >
                      <template #[`item.linha`]="{ item }">{{
                        item.linha || "—"
                      }}</template>
                    </v-data-table>
                  </v-card>
                  <v-card
                    v-if="atualizacao && alteracoes.length"
                    outlined
                    class="mb-4"
                  >
                    <v-card-title class="text-subtitle-2 py-3 d-flex flex-wrap">
                      {{ t("alteracoesTitulo") }}
                      <v-spacer />
                      <v-text-field
                        v-if="alteracoes.length > 10"
                        v-model="buscaAlteracoes"
                        :label="t('buscarAlteracoes')"
                        prepend-inner-icon="mdi-magnify"
                        class="excel-import-busca"
                        clearable
                        outlined
                        dense
                        hide-details
                      />
                    </v-card-title>
                    <div class="text-caption excel-import-subtitle px-4 pb-3">
                      {{
                        t("alteracoesResumo", {
                          quantidade: alteracoes.length,
                          total: resultadoValidacao.totalAlteracoes,
                          registros: resultadoValidacao.registrosComAlteracao,
                        })
                      }}
                    </div>
                    <v-data-table
                      :headers="headersAlteracoes"
                      :items="alteracoes"
                      :search="buscaAlteracoes || ''"
                      :items-per-page="10"
                      :footer-props="paginacao"
                      dense
                    />
                  </v-card>
                  <v-card v-if="registros.length" outlined>
                    <v-card-title class="text-subtitle-2 py-3">{{
                      t("previaTitulo")
                    }}</v-card-title>
                    <div class="text-caption excel-import-subtitle px-4 pb-3">
                      {{
                        t("previaResumo", {
                          quantidade: registros.length,
                          total: resultadoValidacao.totalRegistros,
                        })
                      }}
                    </div>
                    <v-data-table
                      :headers="
                        configuracao.headersPrevia || headersPreviaPadrao
                      "
                      :items="registros"
                      :items-per-page="10"
                      :footer-props="paginacao"
                      dense
                    />
                  </v-card>
                </template>
              </v-stepper-content>

              <v-stepper-content step="3" class="pa-8 text-center">
                <v-icon size="72" class="excel-import-sucesso"
                  >mdi-check-circle-outline</v-icon
                >
                <div class="text-h5 font-weight-bold mt-4">
                  {{ configuracao.textoConcluido || modo("concluido") }}
                </div>
                <p class="text-body-1 excel-import-subtitle mt-2">
                  {{ textoSucesso }}
                </p>
                <v-btn
                  class="
                    ma-1
                    mt-3
                    font-weight-bold
                    text-none
                    btn-primary
                    bg-gradient-primary
                    px-6
                  "
                  height="39"
                  @click="fechar"
                  >{{ t("fecharAtualizar") }}</v-btn
                >
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card-text>
        <v-divider v-if="passo < 3" />
        <v-card-actions v-if="passo < 3" class="crud-form-actions">
          <v-btn
            class="
              ma-1
              font-weight-bold
              text-capitalize
              btn-ls btn-secondary
              bg-gradient-light
              px-6
            "
            height="39"
            :disabled="ocupado"
            @click="passo === 1 ? fechar() : voltar()"
            ><v-icon left small>{{
              passo === 1 ? "mdi-close" : "mdi-arrow-left"
            }}</v-icon
            >{{ passo === 1 ? t("cancelar") : t("voltar") }}</v-btn
          >
          <v-btn
            v-if="passo === 2"
            class="
              ma-1
              font-weight-bold
              text-none
              btn-primary
              bg-gradient-primary
              px-6
              ms-3
            "
            height="39"
            :loading="aplicando"
            :disabled="!podeAplicar || (ocupado && !aplicando)"
            @click="confirmarAplicacao"
            ><v-icon left>mdi-check-circle-outline</v-icon>
            {{ textoConfirmar }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import * as XLSX from "xlsx";

export default {
  name: "ImportacaoExcelLote",
  props: {
    configuracao: { type: Object, required: true },
    dataService: { type: Object, required: true },
    permissao: { type: String, required: true },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      dialog: false,
      passo: 1,
      arquivo: null,
      modeloBaixado: false,
      arrastando: false,
      gerando: false,
      validando: false,
      confirmando: false,
      aplicando: false,
      erro: "",
      resultadoValidacao: null,
      resultadoAplicacao: null,
      requisicao: 0,
      filtro: {},
      selecao: null,
      consultando: false,
      consultaId: 0,
      buscaAlteracoes: "",
    };
  },
  computed: {
    headersAlteracoes() {
      return [
        { text: this.coluna("codigo"), value: "codigo", width: 110 },
        { text: this.coluna("registro"), value: "descricao", width: 170 },
        { text: this.coluna("campo"), value: "campo", width: 190 },
        {
          text: this.coluna("valorAnterior"),
          value: "valorAnterior",
          sortable: false,
        },
        { text: this.coluna("novoValor"), value: "novoValor", sortable: false },
      ];
    },
    headersErros() {
      return [
        { text: this.coluna("aba"), value: "aba", width: 110 },
        { text: this.coluna("linha"), value: "linha", width: 75 },
        { text: this.coluna("campo"), value: "campo", width: 190 },
        {
          text: this.coluna("comoCorrigir"),
          value: "mensagem",
          sortable: false,
        },
      ];
    },
    headersPreviaPadrao() {
      return [
        { text: this.coluna("codigo"), value: "codigo" },
        { text: this.coluna("descricao"), value: "descricao" },
      ];
    },
    paginacao() {
      return {
        itemsPerPageOptions: [10, 25, 50],
        itemsPerPageText: this.t("registrosPorPagina"),
      };
    },
    textoConfirmar() {
      const base = this.atualizacao ? "aplicarAlteracoes" : "confirmarCadastro";
      return this.podeAplicar
        ? this.t(`${base}Quantidade`, { quantidade: this.quantidadeAcao })
        : this.t(base);
    },
    atualizacao() {
      return this.configuracao.modo === "atualizacao";
    },
    limiteRegistros() {
      return this.configuracao.limiteRegistros || 20000;
    },
    selecaoValida() {
      return (
        this.selecao &&
        this.selecao.totalElements > 0 &&
        this.selecao.totalElements <= this.limiteRegistros
      );
    },
    quantidadeAcao() {
      return (
        (this.atualizacao
          ? this.resultadoValidacao?.registrosComAlteracao
          : this.resultadoValidacao?.totalRegistros) || 0
      );
    },
    semAlteracoes() {
      return (
        this.atualizacao &&
        this.resultadoValidacao?.valido === true &&
        !this.quantidadeAcao
      );
    },
    alteracoes() {
      return this.resultadoValidacao?.alteracoes || [];
    },
    // Atualização que também inclui e exclui (ex.: lançamentos da folha): contagem por ação.
    resumoAcoes() {
      const r = this.resultadoValidacao;
      if (!this.atualizacao || !r?.valido || !this.comInclusaoExclusao(r))
        return null;
      return [
        ["Inclusoes", r.registrosCriados, "mdi-plus-circle-outline"],
        ["Alteracoes", r.registrosAtualizados, "mdi-pencil-outline"],
        ["Exclusoes", r.registrosExcluidos, "mdi-delete-outline"],
      ].map(([chave, quantidade, icone]) => ({
        chave: chave.toLowerCase(),
        quantidade: quantidade || 0,
        icone,
        texto: this.t(`resumo${chave}`),
      }));
    },
    textoSucesso() {
      const r = this.resultadoAplicacao;
      if (this.atualizacao && this.comInclusaoExclusao(r))
        return this.t("sucessoAtualizacaoResumo", this.parametrosResumo(r));
      return this.modo("sucesso", {
        quantidade: r
          ? this.atualizacao
            ? r.registrosAtualizados
            : r.registrosCriados
          : 0,
      });
    },
    ocupado() {
      return (
        this.gerando ||
        this.validando ||
        this.confirmando ||
        this.aplicando ||
        this.consultando
      );
    },
    limiteMB() {
      return this.configuracao.limiteMB || 15;
    },
    errosValidacao() {
      return this.resultadoValidacao?.erros || [];
    },
    registros() {
      return this.resultadoValidacao?.registros || [];
    },
    situacaoValidacao() {
      if (this.semAlteracoes)
        return { tipo: "info", icone: "mdi-information-outline" };
      if (this.podeAplicar)
        return { tipo: "success", icone: "mdi-check-circle-outline" };
      return { tipo: "warning", icone: "mdi-alert-outline" };
    },
    podeAplicar() {
      return (
        !!this.arquivo &&
        this.resultadoValidacao?.valido === true &&
        this.quantidadeAcao > 0 &&
        !this.errosValidacao.length &&
        !this.resultadoAplicacao
      );
    },
  },
  watch: {
    filtro: {
      deep: true,
      handler() {
        if (!this.atualizacao) return;
        this.consultaId += 1;
        this.selecao = null;
        this.consultando = false;
        this.arquivo = null;
        this.resultadoValidacao = null;
      },
    },
  },
  beforeDestroy() {
    this.requisicao += 1;
    this.consultaId += 1;
  },
  methods: {
    t(chave, params) {
      return this.$t(`importacaoExcel.assistente.${chave}`, params);
    },
    // Texto do modo atual: <chave>Cadastro ou <chave>Atualizacao.
    modo(chave, params) {
      return this.t(
        `${chave}${this.atualizacao ? "Atualizacao" : "Cadastro"}`,
        params
      );
    },
    coluna(chave) {
      return this.$t(`importacaoExcel.colunas.${chave}`);
    },
    numero(valor) {
      return Number(valor).toLocaleString(this.$i18n.locale);
    },
    comInclusaoExclusao(resultado) {
      return !!(resultado?.registrosCriados || resultado?.registrosExcluidos);
    },
    parametrosResumo(resultado) {
      return {
        inclusoes: this.numero(resultado.registrosCriados || 0),
        alteracoes: this.numero(resultado.registrosAtualizados || 0),
        exclusoes: this.numero(resultado.registrosExcluidos || 0),
      };
    },
    abrir() {
      this.passo = 1;
      this.arquivo = null;
      this.erro = "";
      this.modeloBaixado = false;
      this.resultadoValidacao = null;
      this.resultadoAplicacao = null;
      this.dialog = true;
      this.filtro = Object.fromEntries(
        (this.configuracao.filtros || []).map((campo) => [
          campo.chave,
          campo.valorInicial ?? null,
        ])
      );
      this.selecao = null;
      // Filtro inicial já vindo da tela: confere a seleção sem exigir o clique.
      if (this.atualizacao && this.configuracao.verificarAoAbrir)
        this.$nextTick(this.verificarSelecao);
    },
    fechar() {
      if (this.ocupado) return;
      this.dialog = false;
      this.requisicao += 1;
      this.consultaId += 1;
      if (this.resultadoAplicacao?.aplicado)
        this.$emit("concluido", this.resultadoAplicacao);
      this.resultadoAplicacao = null;
    },
    voltar() {
      if (this.ocupado) return;
      this.passo = 1;
      this.erro = "";
    },
    async gerarModelo() {
      if (this.ocupado || (this.atualizacao && !this.selecaoValida)) return;
      const id = ++this.requisicao;
      this.gerando = true;
      this.erro = "";
      try {
        const response = await this.dataService.generateImportTemplate({
          filtro: this.filtro,
        });
        if (id !== this.requisicao) return;
        this.baixar(
          response.data,
          this.configuracao.nomeArquivo || "cadastro-modelo.xlsx"
        );
        this.modeloBaixado = true;
        this.arquivo = null;
        this.resultadoValidacao = null;
        this.passo = 2;
      } catch (error) {
        if (id === this.requisicao)
          await this.mostrarErro(error, this.t("erroModelo"));
      } finally {
        if (id === this.requisicao) this.gerando = false;
      }
    },
    async verificarSelecao() {
      if (this.ocupado) return;
      const id = ++this.consultaId;
      this.consultando = true;
      this.erro = "";
      try {
        const response = await this.dataService.listImportRecords(
          { ...this.filtro },
          { page: 0, linesPerPage: 10 }
        );
        if (id === this.consultaId) this.selecao = response.data;
      } catch (error) {
        if (id === this.consultaId) {
          this.selecao = null;
          await this.mostrarErro(error, this.t("erroConsulta"));
        }
      } finally {
        if (id === this.consultaId) this.consultando = false;
      }
    },
    soltarArquivo(event) {
      this.arrastando = false;
      if (this.ocupado) return;
      const files = event.dataTransfer?.files;
      if (files?.length !== 1) {
        this.arquivoAlterado(null);
        this.erro = this.t("erroUmArquivo");
        return;
      }
      this.arquivoAlterado(files[0]);
    },
    async arquivoAlterado(file) {
      if (this.ocupado) return;
      this.requisicao += 1;
      this.arquivo = null;
      this.resultadoValidacao = null;
      this.resultadoAplicacao = null;
      this.erro = "";
      if (!file) return;
      if (!String(file.name).toLowerCase().endsWith(".xlsx")) {
        this.erro = this.t("erroFormato");
        return;
      }
      if (!file.size || file.size > this.limiteMB * 1024 * 1024) {
        this.erro = this.t("erroTamanho", { mb: this.limiteMB });
        return;
      }
      this.arquivo = file;
      await this.validarArquivo();
    },
    async validarArquivo() {
      if (!this.arquivo || this.ocupado) return;
      const id = ++this.requisicao;
      this.validando = true;
      this.erro = "";
      this.resultadoValidacao = null;
      this.buscaAlteracoes = "";
      try {
        const response = await this.dataService.validateImport(this.arquivo);
        if (id === this.requisicao) this.resultadoValidacao = response.data;
      } catch (error) {
        if (id === this.requisicao)
          await this.mostrarErro(error, this.t("erroConferencia"));
      } finally {
        if (id === this.requisicao) this.validando = false;
      }
    },
    async confirmarAplicacao() {
      if (!this.podeAplicar || this.ocupado) return;
      const id = this.requisicao;
      this.confirmando = true;
      try {
        const confirmation = await this.$swal.fire({
          title: this.t(
            this.atualizacao
              ? "confirmarAtualizacaoTitulo"
              : "confirmarCadastroTitulo"
          ),
          text: this.resumoAcoes
            ? this.t(
                "confirmarAtualizacaoResumo",
                this.parametrosResumo(this.resultadoValidacao)
              )
            : this.t(
                this.atualizacao
                  ? "confirmarAtualizacaoTexto"
                  : "confirmarCadastroTexto",
                { quantidade: this.quantidadeAcao }
              ),
          icon: "question",
          showCancelButton: true,
          confirmButtonText: this.t(
            this.atualizacao ? "simAtualizar" : "simCadastrar"
          ),
          cancelButtonText: this.t("voltarConferencia"),
        });
        if (!confirmation.isConfirmed || id !== this.requisicao) return;
        this.aplicando = true;
        this.erro = "";
        const response = await this.dataService.applyImport(this.arquivo);
        if (id !== this.requisicao) return;
        if (!response.data?.valido || !response.data?.aplicado) {
          this.resultadoValidacao = response.data;
          this.erro = this.t("novaConferenciaErros");
          return;
        }
        this.resultadoAplicacao = response.data;
        this.passo = 3;
      } catch (error) {
        if (id !== this.requisicao) return;
        this.resultadoValidacao = null;
        await this.mostrarErro(error, this.t("erroAplicacao"));
      } finally {
        if (id === this.requisicao) {
          this.confirmando = false;
          this.aplicando = false;
        }
      }
    },
    baixarErros() {
      const rows = this.errosValidacao.map((item) => ({
        [this.coluna("aba")]: item.aba,
        [this.coluna("linha")]: item.linha,
        [this.coluna("campo")]: item.campo,
        [this.coluna("comoCorrigir")]: item.mensagem,
      }));
      const workbook = XLSX.utils.book_new();
      const sheet = XLSX.utils.json_to_sheet(rows);
      sheet["!cols"] = [{ wch: 16 }, { wch: 9 }, { wch: 35 }, { wch: 100 }];
      XLSX.utils.book_append_sheet(
        workbook,
        sheet,
        this.t("planilhaInconsistencias")
      );
      XLSX.writeFile(workbook, "importacao-inconsistencias.xlsx");
    },
    baixar(content, name) {
      const url = URL.createObjectURL(
        content instanceof Blob ? content : new Blob([content])
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    async mostrarErro(error, fallback) {
      let data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          data = JSON.parse(await data.text());
        } catch (_) {
          data = null;
        }
      }
      this.erro = data?.message || data?.errors?.[0]?.message || fallback;
    },
  },
};
</script>

<style scoped>
.excel-import-card {
  border-radius: 16px !important;
}
.excel-import-header {
  background: #f8fafc;
  border-bottom: 1px solid #e7eaf0;
  word-break: normal;
}
.excel-import-subtitle {
  color: #667085;
}
.excel-import-stepper {
  box-shadow: none;
}
.excel-import-stepper::v-deep .v-stepper__header {
  box-shadow: none;
  border-bottom: 1px solid #edf0f5;
}
/* Cores explícitas: o tema do Vuetify está desabilitado e "primary" não tem cor. */
.excel-import-stepper::v-deep .v-stepper__step--active .v-stepper__step__step,
.excel-import-stepper::v-deep
  .v-stepper__step--complete
  .v-stepper__step__step {
  background: #525863 !important;
  border-color: #525863 !important;
  color: #fff !important;
}
.excel-import-stepper::v-deep
  .v-stepper__step--complete
  .v-stepper__step__step
  .v-icon {
  color: #fff !important;
}
.excel-import-numero {
  background: #525863;
  color: #fff;
  font-weight: 700;
}
.excel-import-sheet {
  border-radius: 12px;
  border-color: #e1e7ef;
}
.excel-import-guide {
  background: #f8fafc;
  border-radius: 12px;
}
.excel-import-drop {
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  background: #f8fafc;
  transition: border-color 0.15s, background 0.15s;
}
.excel-import-drop--active {
  border-color: #525863;
  background: #f1f3f6;
}
.excel-import-aviso {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  border: 1px solid;
  border-radius: 12px;
  font-size: 0.875rem;
  line-height: 1.45;
}
.excel-import-aviso .v-icon {
  color: inherit !important;
  margin-top: 2px;
}
.excel-import-aviso--primary,
.excel-import-aviso--info {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #1e40af;
}
.excel-import-aviso--success {
  background: #ecfdf3;
  border-color: #abefc6;
  color: #05603a;
}
.excel-import-aviso--warning {
  background: #fffaeb;
  border-color: #fedf89;
  color: #93370d;
}
.excel-import-aviso--error {
  background: #fef3f2;
  border-color: #fecdca;
  color: #b42318;
}
.excel-import-card::v-deep .v-text-field--outlined .v-input__slot {
  border: 1px solid #cbd5e1 !important;
  background: #fff;
}
.excel-import-resumo {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.excel-import-resumo__item {
  align-items: center;
  border: 1px solid;
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.8125rem;
  gap: 6px;
  padding: 4px 12px;
}
.excel-import-resumo__item .v-icon {
  color: inherit !important;
}
.excel-import-resumo__item--inclusoes {
  background: #ecfdf3;
  border-color: #abefc6;
  color: #05603a;
}
.excel-import-resumo__item--alteracoes {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #1e40af;
}
.excel-import-resumo__item--exclusoes {
  background: #fef3f2;
  border-color: #fecdca;
  color: #b42318;
}
.excel-import-busca {
  max-width: 280px;
}
.excel-import-card::v-deep .v-data-table td {
  white-space: normal;
  overflow-wrap: break-word;
}
.excel-import-sucesso {
  color: #12b76a !important;
}
</style>
